/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import {
  CcfAccordion,
  CcfAccordionDetails,
  CcfAccordionSummary,
  CcfApprovedIcon,
  CcfCopilotSuggestionIcon,
  CcfDoubleArrowIcon,
  CcfIlluminateStarIcon,
  CcfTypography,
  CcfUpArrowIcon,
  useTranslator
} from '@nice-ccf/ui-controls';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeContactSelector,
  dispositionInteractionActions,
  getApplicationDirection,
  getDispositionData,
  getFinalSummary,
  getcopilotFlowMsgIndex,
  setFinalSummary,
} from '@nice-ccf/ui-components';
import styles from '../ccf-disposition.module.scss';
//import { interactionTab } from '../../ccf-interaction-workspace/ccf-interaction-space.slice';
import {
  DigitalChannelType,
  MediaType,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';
import dispositionAccordionStyles from '../ccf-disposition-accordion.styles';
import { copilotAgentAndCustomerChatData } from '../../ccf-app-space/ccf-copilot/ccf-copilot-chat/mock-data/custom-flow';
import { callApi } from '../../ccf-app-space/ccf-copilot/ccf-copilot-api';
import { processFinalSummary } from '../../ccf-app-space/ccf-copilot/ccf-copilot-util';

export interface CcfLiveDispositionInteractionAccordionDetailsProps {
  allTabs: string[];
  clickedResolved?: boolean;
  contactId?: string;
  copilotAssist?: string;
  contactMedia?: string;
  type: string;
  validateFields?: any;
}

interface DispositionDataObjectType {
  Static: {
    priority: { value: string };
    status: { value: string };
    type: { value: string };
  };
  [MediaType.VOICE]?: {
    markcontactas: { value: string };
    tags: { value: string };
    notes: { value: string };
  };
  [MediaType.DIGITAL]?: {
    all: {
      value: string;
    };
    sms?: {
      value: string;
    };
    Email?: {
      value: string;
    };
    Twitter?: {
      value: string;
    };
    Facebook?: {
      value: string;
    };
    Linkedin?: {
      value: string;
    };
    Instagram?: {
      value: string;
    };
    Chat?: {
      value: string;
    };
  };
}

/**
 * Component displays accordion details for disposition
 * @param props -CcfLiveDispositionInteractionAccordionDetailsProps
 * @returns displays accordion details for disposition
 * @example <CcfLiveDispositionInteractionAccordionDetails/>
 */
export function CcfLiveDispositionInteractionAccordionDetails(
  props: CcfLiveDispositionInteractionAccordionDetailsProps
) {
  const [translate] = useTranslator();
  const activeContact = useSelector(activeContactSelector);
  const theme = useTheme();
  const stylesAccordion = dispositionAccordionStyles(theme);
  const accordionTextObj: any = {
    media: {
      [MediaType.DIGITAL]: {
        caption: activeContact?.customer?.firstName + ' ' + activeContact?.customer?.lastName as string,
        subText: translate('digital'),
        subTypes: {
          sms: {
            caption: 'SMS',
          },
          Email: {
            caption: 'Email',
          },
          Twitter: {
            caption: 'Twitter',
          },
          Facebook: {
            caption: 'Facebook',
          },
          Linkedin: {
            caption: 'Linkedin',
          },
          Instagram: {
            caption: 'Instagram',
          },
          Chat: {
            caption: 'Chat',
          },
        },
      },
      [MediaType.VOICE]: {
        caption:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? (activeContact?.customer?.firstName + ' ' + activeContact?.customer?.lastName as string)
            : 'Inbound Voice',
        subText:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? 'Digital'
            : (activeContact?.customer.phoneNo as string),
      },
      [MediaType.VIDEOCALL]: {
        caption:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? (activeContact?.customer?.firstName + ' ' + activeContact?.customer?.lastName  as string)
            : 'Inbound Video Call',
        subText:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? 'Digital'
            : (activeContact?.customer.phoneNo as string),
      },
      [MediaType.VOICEMAIL]: {
        caption:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? (activeContact?.customer?.firstName + ' ' + activeContact?.customer?.lastName  as string)
            : 'Inbound Voice Mail',
        subText:
          props.contactMedia === MediaType.DIGITAL && props.allTabs.length
            ? 'Digital'
            : (activeContact?.customer.phoneNo as string),
      },
    },
    static: {
      caption: 'CRM',
      subText: 'ZenDesk',
    },
  };

  const applicationDirection = useSelector(getApplicationDirection);
  const [isExpanded, setIsExpanded] = useState(true);
  const finalSummary = useSelector(getFinalSummary);
  const [outcomes,setOutcomes] = useState(finalSummary);
  const dispatch = useDispatch();
  const flowMsgIndex = useSelector(getcopilotFlowMsgIndex);
  const userReplyChatData = activeContact?.customerChatReplyJsonData?.length ?? 0;
  const lastindexVal = activeContact?.customerChatReplyLastindexVal ?? 0;
  const isConversationCompleted = userReplyChatData === lastindexVal;
  // const [showSpinner, setShowSpinner] = useState(
  //   (activeContact?.autoSummary !== 'Off' &&
  //     activeContact?.media === MediaType.DIGITAL &&
  //     activeContact?.channel === 'Chat' &&
  //     flowMsgIndex >= copilotAgentAndCustomerChatData.length - 1) ||
  //     (activeContact?.autoSummary !== 'Off' &&
  //       activeContact?.media === MediaType.VOICE)
  //     ? true
  //     : false
  // );
  const shouldShowSpinnerInitially = (contact: any) => {
    return (
      contact?.autoSummary !== 'Off' ||
      (contact?.autoSummary === 'Off' &&
        contact?.channel === 'Email' &&
        contact?.copilotSource === 'Live LLM')
    );
  };
  const [showSpinner, setShowSpinner] = useState(shouldShowSpinnerInitially(activeContact));

  const notesDefaultValue =
    'The customer called to request new insurance cards for their daughter, mentioning their employment with the company. The agent confirmed the policy number and offered assistance with ordering the cards. The agent provided information about the delivery timeframe of 10 business days. They also informed the customer about eligibility for a covered annual physical. The issue was resolved successfully.';
  activeContact?.autoSummary !== 'Off' &&
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (
        shouldShowSpinnerInitially(activeContact) &&
        activeContact?.media === MediaType.DIGITAL &&
        (activeContact?.channel === 'Chat' || activeContact?.channel === 'Email') &&
        flowMsgIndex >= copilotAgentAndCustomerChatData.length
      ) {
        setTimeout(() => {
          setShowSpinner(false);
        }, 5000);
      }
    }, []);

  useEffect(() => {
    if (
      activeContact?.autoSummary !== 'Off' &&
      activeContact?.media === MediaType.VOICE &&
      activeContact?.contactStatus === VoiceContactStatus.DISCONNECTED
    ) {
      setTimeout(() => {
        setShowSpinner(false);
      }, 5000);
    }
  }, [activeContact?.contactStatus]);

  useEffect(() => {
    if (
      activeContact?.copilotSource === 'Live LLM'
    ) {
      setOutcomes(finalSummary);
      setTimeout(() => {
        setShowSpinner(false);
      }, 2000);
    }
  }, [finalSummary]);

  /***
   * @example getText()
   */
  const getText = (type: string) =>
    props.type === 'media' && activeContact
      ? accordionTextObj.media[activeContact.media][type]
      : accordionTextObj.static[type];
  const [toggleExpandAndCollaspe, setToggleExpandAndCollaspe] = useState(true);
  const { setDispositionData, setDispositionResolution } =
    dispositionInteractionActions;
  const dispositionData = useSelector(getDispositionData);


  useEffect(() => {
    const newObj: DispositionDataObjectType = {
      Static: {
        priority: { value: '' },
        status: { value: '' },
        type: { value: '' },
      },
    };

    if (props?.contactMedia === MediaType.VOICE) {
      newObj[MediaType.VOICE] = {
        markcontactas: { value: '' },
        tags: { value: '' },
        notes: { value: '' },
      };
    } else if (props?.contactMedia === MediaType.DIGITAL) {
      newObj[MediaType.DIGITAL] = props.allTabs.reduce((a: any, c) => {
        a[c] = { value: '' };
        return a;
      }, {});
    }

    if (!dispositionData[props.contactId as string]) {
      dispatch(
        setDispositionData({
          [props.contactId as string]: {
            isResolved: false,
            ...newObj,
          },
        })
      );
    }
  }, []);

  useEffect(() => {
    setIsExpanded(true);
  }, [props.clickedResolved]);

  /**
   * Function to handle change of dropdown
   * @param event - any
   * @param caption - string
   * @param selectRef - any
   * @example - handleChange
   */
  const handleChange = (event: any, tabType: string, field: string) => {
    const value = event.target.value;
    // if (value !== '') {
    //   caption === 'DESCRIPTION'
    //     ? event.target.classList.remove('customDropdownForError')
    //     : caption === 'OUTCOME'
    //     ? selectRef.current.children[0]?.children[0]?.classList.remove(
    //         'customDropdownForError'
    //       )
    //     : selectRef.current.children[2]?.children[0]?.classList.remove(
    //         'customDropdownForError'
    //       );
    // }
    dispatch(
      setDispositionData({
        ...dispositionData,
        [props.contactId as string]: {
          ...dispositionData[props.contactId as string],
          [tabType]: {
            ...dispositionData[props.contactId as string][tabType],
            [field]: { value },
            ...(tabType === MediaType.DIGITAL &&
              field === 'all' &&
              props.allTabs.reduce((a: any, tab: string) => {
                a[tab] = { value };
                return a;
              }, {})),
          },
        },
      })
    );

    const updatedOutcomes = {
      ...outcomes,
      resolved: value === 'Resolved' ? true : false,
    };

    dispatch(setFinalSummary(updatedOutcomes));
    const mediaType = activeContact?.media;
    props.validateFields(value, field, tabType, mediaType);
    dispatch(
      setDispositionResolution({
        contactId: props.contactId,
        isResolved: true,
      })
    );
  };

  /**
   * Function to handle accordion change
   * @example - handleAccordionChange
   */
  const handleAccordionChange = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Function to handle exapnad and collapse of acccordion
   @example - handleExapndAndCollapse 
   */
  const handleExpandAndCollapse = () => {
    setToggleExpandAndCollaspe(!toggleExpandAndCollaspe);
  };

  /**
   * Function to handle input value for dropdown
   * @param field - string
   * @returns - input value for dropdown
   * @example - inputValue
   */
  const inputValue = (tabType: string, field: string) =>
    dispositionData[props.contactId as string] &&
    dispositionData[props.contactId as string][tabType]
      ? dispositionData[props.contactId as string][tabType][field]?.value
      : '';

  return (
    <CcfAccordion
      sx={stylesAccordion.accordionContainer}
      expanded={isExpanded}
      onChange={handleAccordionChange}
    >
      <CcfAccordionSummary
        expandIcon={<CcfDoubleArrowIcon sx={stylesAccordion.expandedIcon} />}
        aria-controls="detail-content"
        id="disposition-header"
        sx={stylesAccordion.accordionHeader}
        onClick={handleExpandAndCollapse}
      >
        <div className="textForDispositionCards">
          <CcfTypography className="textForDispositionDetails">
            {getText('caption')}
          </CcfTypography>
          <CcfTypography className="subTextForDispositionDetails">
            {getText('subText')}
          </CcfTypography>
        </div>
      </CcfAccordionSummary>
      <div style={{ display: 'flex' }}>
        <CcfAccordionDetails sx={stylesAccordion.addressDetails}>
          {props.type === 'media' && (
            <>
              {(props.contactMedia === MediaType.VOICE ||
                (activeContact?.media === MediaType.DIGITAL &&
                  (activeContact?.channel === 'Chat' || activeContact?.channel === 'Email') &&
                  activeContact?.copilotAssist === 'Yes')) && (
                <>
                  <CcfTypography sx={stylesAccordion.captionForDropdown}>
                  {translate('status')} <span style={{ color: 'red' }}>*</span>
                  </CcfTypography>
                  <Select
                    id="status"
                    sx={stylesAccordion.customDropdown}
                    value={ 
                      activeContact?.copilotSource !== 'Live LLM' ? 
                      inputValue(MediaType.VOICE, 'disposition') :
                      (outcomes.resolved === true) ? 'Resolved': 'Pending'
                    }
                    onChange={(e) =>
                      handleChange(e, MediaType.VOICE, 'disposition')
                    }
                    disableUnderline
                    IconComponent={() => (
                      <CcfUpArrowIcon className="arrowIconForDropdown" />
                    )}
                    displayEmpty
                  >
                    <MenuItem
                      classes={{
                        root: styles.dropdownOption,
                      }}
                      value={undefined}
                      hidden
                      disabled
                    >
                      <span className="placeholderText">{translate('selectItem')}</span>
                    </MenuItem>
                    <MenuItem
                      classes={{
                        root: styles.dropdownOption,
                      }}
                      value={'Resolved'}
                    >
                      {translate('resolved')}
                    </MenuItem>
                    <MenuItem
                      classes={{
                        root: styles.dropdownOption,
                      }}
                      value={'Pending'}
                    >
                      {translate('pending')}
                    </MenuItem>
                  </Select>
                  {activeContact?.copilotSource !== 'Live LLM' && (
                    <>
                      <CcfTypography sx={stylesAccordion.captionForDropdown}>
                      {translate('disposition')} <span style={{ color: 'red' }}>*</span>
                      </CcfTypography>
                      <Select
                        id="disposition"
                        sx={stylesAccordion.customDropdown}
                        value={inputValue(MediaType.VOICE, 'tags')}
                        onChange={(e) =>
                          handleChange(e, MediaType.VOICE, 'tags')
                        }
                        disableUnderline
                        IconComponent={() => (
                          <CcfUpArrowIcon className="arrowIconForDropdown" />
                        )}
                        displayEmpty
                      >
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value=""
                          hidden
                          disabled
                        >
                          <span className="placeholderText">{translate('selectItem')}</span>
                        </MenuItem>
                        {activeContact?.copilotSource !== 'Live LLM' && (
                          <MenuItem
                            classes={{
                              root: styles.dropdownOption,
                            }}
                            value={'travelReservation'}
                          >
                         {translate('travelReservation')}
                          </MenuItem>
                        )}
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={'Yes'}
                        >
                          {translate('yes')}
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={'No'}
                        >
                         {translate('no')}
                        </MenuItem>
                      </Select>
                    </>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginTop: '5px',
                    }}
                  >
                    <CcfTypography sx={stylesAccordion.captionForDropdown}>
                    {translate('notes')}
                    </CcfTypography>
                    {shouldShowSpinnerInitially(activeContact) &&
                      !showSpinner &&
                      flowMsgIndex >=
                        copilotAgentAndCustomerChatData.length - 1 && (
                        <div className="autoSummaryChip">
                          <CcfApprovedIcon className="approvedIcon" />
                          <CcfTypography className="txtautoSummaryCompleted">
                          {translate('autoSummaryCompleted')}
                          </CcfTypography>
                        </div>
                      )}
                  </div>

                  <div
                    className={
                      showSpinner ? 'disposition__textarea_for_autosummary' : ''
                    }
                  >
                    {' '}
                    {shouldShowSpinnerInitially(activeContact) && showSpinner ? (
                      <Box mt={3} width="100%" className="spinner-block">
                        <CircularProgress
                          size={20}
                          sx={{
                            '& .MuiCircularProgress-circle': {
                              color: '#000000',
                            },
                          }}
                          variant="indeterminate"
                        />
                        <CcfCopilotSuggestionIcon className="copilotIcon" />
                        <CcfTypography className="spinner_text">
                          {' '}
                          {`${translate('generatingSummary')}...`} <br />
                          {translate('summaryGenerationMsg')}
                        </CcfTypography>
                      </Box>
                    ) : (
                      <textarea
                        className="disposition__textarea"
                        placeholder={translate('enterNotes')}
                        onChange={(e) =>
                          handleChange(e, MediaType.VOICE, 'notes')
                        }
                        style={{
                          height:
                            shouldShowSpinnerInitially(activeContact)
                              ? '190px'
                              : '80px',
                        }}
                        defaultValue={
                          activeContact?.copilotSource === 'Live LLM'
                            ?  outcomes.summary
                            : activeContact?.media === MediaType.DIGITAL &&
                              activeContact?.channel === 'Chat' &&
                              flowMsgIndex <
                                copilotAgentAndCustomerChatData.length - 1
                            ? ''
                            : activeContact?.autoSummary === 'On'
                            ? activeContact.autoSummaryText
                            : activeContact?.autoSummary === 'Custom'
                            ? activeContact.autoSummaryText
                            : inputValue(MediaType.VOICE, 'notes')
                        }
                        // value={finalSummary}
                      ></textarea>
                    )}
                  </div>
                </>
              )}

              {(((activeContact?.media === MediaType.DIGITAL ||
                props.contactMedia === MediaType.DIGITAL) &&
                activeContact?.channel !== 'Chat') ||
                ((activeContact?.channel === 'Chat' || activeContact?.channel === 'Email') &&
                  activeContact?.copilotAssist === 'No')) && (
                <>
                  {props.allTabs && props.allTabs.length > 1 && (
                    <>
                      <CcfTypography sx={stylesAccordion.captionForDropdown}>
                        {translate('markAllAs')}
                      </CcfTypography>
                      <Select
                        id="markAllAs"
                        sx={stylesAccordion.customDropdown}
                        value={inputValue(MediaType.DIGITAL, 'all')}
                        onChange={(e) =>
                          handleChange(e, MediaType.DIGITAL, 'all')
                        }
                        disableUnderline
                        IconComponent={() => (
                          <CcfUpArrowIcon className="arrowIconForDropdown" />
                        )}
                        displayEmpty
                      >
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={props.allTabs.length > 1 ? '' : undefined}
                          hidden
                          disabled
                        >
                          <span className="placeholderText">{translate('selectItem')}</span>
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={'Resolved'}
                        >
                          {translate('resolved')}
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={'Pending'}
                        >
                          {translate('pending')}
                        </MenuItem>
                      </Select>
                      {props.allTabs.map((tab, index) => (
                        <div className="dynamicTabsDropDown">
                          <CcfTypography
                            sx={stylesAccordion.captionForDropdown}
                          >
                            {`${
                              accordionTextObj.media[MediaType.DIGITAL]
                                .subTypes[tab].caption
                            } as`}
                          </CcfTypography>
                          <Select
                            id={tab}
                            sx={stylesAccordion.customDropdown}
                            value={inputValue(MediaType.DIGITAL, tab)}
                            onChange={(e) =>
                              handleChange(e, MediaType.DIGITAL, tab)
                            }
                            disableUnderline
                            IconComponent={() => (
                              <CcfUpArrowIcon className="arrowIconForDropdown" />
                            )}
                            displayEmpty
                          >
                            <MenuItem
                              classes={{
                                root: styles.dropdownOption,
                              }}
                              value={index === 0 ? '' : undefined}
                              hidden
                              disabled
                            >
                              <span className="placeholderText">
                                {translate('selectItem')}
                              </span>
                            </MenuItem>
                            <MenuItem
                              classes={{
                                root: styles.dropdownOption,
                              }}
                              value={'Resolved'}
                            >
                              {translate('resolved')}
                            </MenuItem>
                            <MenuItem
                              classes={{
                                root: styles.dropdownOption,
                              }}
                              value={'Pending'}
                            >
                              {translate('pending')}
                            </MenuItem>
                          </Select>
                        </div>
                      ))}
                    </>
                  )}
                  {props.allTabs.length === 1 && accordionTextObj.media[MediaType.DIGITAL].subTypes[props.allTabs[0]].caption !== 'Email' && (
                    <>
                      <CcfTypography sx={stylesAccordion.captionForDropdown}>
                        {`Mark ${
                          accordionTextObj.media[MediaType.DIGITAL].subTypes[
                            props.allTabs[0]
                          ].caption
                        } as`}
                      </CcfTypography>
                      <Select
                        id={props.allTabs[0]}
                        sx={stylesAccordion.customDropdown}
                        value={inputValue(MediaType.DIGITAL, props.allTabs[0])}
                        onChange={(e) =>
                          handleChange(e, MediaType.DIGITAL, props.allTabs![0])
                        }
                        disableUnderline
                        IconComponent={() => (
                          <CcfUpArrowIcon className="arrowIconForDropdown" />
                        )}
                        displayEmpty
                      >
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={undefined}
                          hidden
                          disabled
                        >
                          <span className="placeholderText">{}</span>
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={'Resolved'}
                        >
                          {translate('resolved')}
                        </MenuItem>
                        <MenuItem
                          classes={{
                            root: styles.dropdownOption,
                          }}
                          value={'Pending'}
                        >
                          {translate('pending')}
                        </MenuItem>
                      </Select>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {props.type === 'static' && (
            <>
              <CcfTypography sx={stylesAccordion.captionForDropdown}>
                {translate('priority')}
              </CcfTypography>
              <Select
                id="priority"
                sx={stylesAccordion.customDropdown}
                value={inputValue('Static', 'priority')}
                onChange={(e) => handleChange(e, 'Static', 'priority')}
                disableUnderline
                IconComponent={() => (
                  <CcfUpArrowIcon className="arrowIconForDropdown" />
                )}
                displayEmpty
              >
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value=""
                  hidden
                  disabled
                >
                  <span className="placeholderText">{translate('selectItem')}</span>
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'High'}
                >
                  {translate('high')}
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'Medium'}
                >
                  {translate('medium')}
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'Low'}
                >
                  {translate('low')}
                </MenuItem>
              </Select>
              <CcfTypography sx={stylesAccordion.captionForDropdown}>
                {translate('status')}
              </CcfTypography>
              <Select
                id="status"
                sx={stylesAccordion.customDropdown}
                value={inputValue('Static', 'status')}
                onChange={(e) => handleChange(e, 'Static', 'status')}
                disableUnderline
                IconComponent={() => (
                  <CcfUpArrowIcon className="arrowIconForDropdown" />
                )}
                displayEmpty
              >
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value=""
                  hidden
                  disabled
                >
                  <span className="placeholderText">{translate('selectItem')}</span>
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'Resolved'}
                >
                  {translate('resolved')}
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'Pending'}
                >
                  {translate('pending')}
                </MenuItem>
              </Select>
              <CcfTypography sx={stylesAccordion.captionForDropdown}>
                {translate('type')}
              </CcfTypography>
              <Select
                id="type"
                sx={stylesAccordion.customDropdown}
                value={inputValue('Static', 'type')}
                onChange={(e) => handleChange(e, 'Static', 'type')}
                disableUnderline
                IconComponent={() => (
                  <CcfUpArrowIcon className="arrowIconForDropdown" />
                )}
                displayEmpty
              >
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value=""
                  hidden
                  disabled
                >
                  <span className="placeholderText">{translate('selectItem')}</span>
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'Yes'}
                >
                  {translate('yes')}
                </MenuItem>
                <MenuItem
                  classes={{
                    root: styles.dropdownOption,
                  }}
                  value={'No'}
                >
                  {translate('no')}
                </MenuItem>
              </Select>
            </>
          )}
        </CcfAccordionDetails>

        {activeContact?.copilotSource === 'Live LLM' && (
          <CcfAccordionDetails sx={stylesAccordion.addressDetails}>
            {activeContact?.copilotSource === 'Live LLM' && (
              <>
                <CcfTypography sx={stylesAccordion.captionForDropdown} mt={0.5}>
                  {translate('intents')} 
                  {/* <span style={{ color: 'red' }}>*</span> */}
                </CcfTypography>
                <textarea
                  className="disposition__textarea"
                  placeholder={translate('enterIntents')}
                  //</>onChange={(e) =>
                  // handleChange(e, MediaType.VOICE, 'notes')
                  //}
                  style={{
                    height: '42px',
                  }}
                  // defaultValue={isConversationCompleted ? outcomes.intents: ''} also to be added on 536 line
                  defaultValue={ outcomes.intents}
                ></textarea>
                <CcfTypography sx={stylesAccordion.captionForDropdown} mt={0.5}>
                  {translate('outcomes')} 
                  {/* <span style={{ color: 'red' }}>*</span> */}
                </CcfTypography>
                <textarea
                  className="disposition__textarea"
                  placeholder={translate('enterOutcomes')}
                  //</>onChange={(e) =>
                  // handleChange(e, MediaType.VOICE, 'notes')
                  //}
                  style={{
                    height: '42px',
                  }}
                  defaultValue={outcomes.outcomes}
                ></textarea>
                <CcfTypography sx={stylesAccordion.captionForDropdown} mt={0.5}>
                  {translate('actions')} 
                  {/* <span style={{ color: 'red' }}>*</span> */}
                </CcfTypography>
                <textarea
                  className="disposition__textarea"
                  placeholder={translate('enterActions')}
                  //</>onChange={(e) =>
                  // handleChange(e, MediaType.VOICE, 'notes')
                  //}
                  style={{
                    height: '42px',
                  }}
                  defaultValue={ outcomes.actions}
                ></textarea>
                <CcfTypography sx={stylesAccordion.captionForDropdown} mt={0.5}>
                  {translate('entities')} 
                  {/* <span style={{ color: 'red' }}>*</span> */}
                </CcfTypography>
                <textarea
                  className="disposition__textarea"
                  placeholder={translate('enterEntities')}
                  //</>onChange={(e) =>
                  // handleChange(e, MediaType.VOICE, 'notes')
                  //}
                  style={{
                    height: '42px',
                  }}
                  defaultValue={outcomes.entities}
                ></textarea>
              </>
            )}
          </CcfAccordionDetails>
        )}
      </div>
    </CcfAccordion>
  );
}

export default CcfLiveDispositionInteractionAccordionDetails;